import {
  emailToSendNotificationsSchema,
  emailToSendNotificationsUI,
  titleUI,
  internationalPhoneSchema,
  internationalPhoneUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import {
  checkboxSchema,
  checkboxUI,
} from 'platform/forms-system/src/js/web-component-patterns/checkboxPattern';
import { validateTelephoneInput } from 'platform/forms-system/src/js/validation';

import TelephoneFieldNoInternalErrors from '../components/TelephoneFieldNoInternalErrors';

import { veteranFields } from '../definitions/constants';

const PHONE_REQUIRED_ERROR =
  'Enter a phone number. At least one domestic or international phone number is required.';
const PRIMARY_PHONE_INVALID_ERROR =
  'Enter a valid United States of America phone number. Use 10 digits.';

const hasPhoneContact = phone =>
  Boolean(phone?.contact && String(phone.contact).trim());

const contactDigitsOnly = contact => String(contact).replace(/\D/g, '');

const hasLeadingUsCountryCode = contact => {
  const digits = contactDigitsOnly(contact);
  return digits.length > 0 && digits.startsWith('1');
};

const getVeteranFromFormData = formData => formData?.veteran ?? formData;

const needsEitherPhone = formData => {
  const veteran = getVeteranFromFormData(formData);
  return (
    !hasPhoneContact(veteran?.homePhone) &&
    !hasPhoneContact(veteran?.alternatePhone)
  );
};

const isPhoneRequired = phoneField => (formData, _index, fullData) => {
  if (needsEitherPhone(fullData ?? formData)) {
    return true;
  }

  const veteran = getVeteranFromFormData(fullData);
  return hasPhoneContact(veteran?.[phoneField]) || hasPhoneContact(formData);
};

const US_PHONE_COUNTRY_ERROR =
  'Select United States of America as the country code.';

const createPhoneValidations = ({
  invalidError,
  validateWhenFilledOnly = false,
  requireUSCountry = false,
} = {}) => [
  (errors, value, formData, _schema, errorMessages) => {
    if (!hasPhoneContact(value)) {
      if (needsEitherPhone(formData)) {
        errors.addError(errorMessages?.required ?? PHONE_REQUIRED_ERROR);
      }
      return;
    }

    if (requireUSCountry && value?.countryCode && value.countryCode !== 'US') {
      errors.addError(invalidError ?? US_PHONE_COUNTRY_ERROR);
      return;
    }

    if (requireUSCountry && hasLeadingUsCountryCode(value.contact)) {
      errors.addError(invalidError ?? PRIMARY_PHONE_INVALID_ERROR);
      return;
    }

    if (validateWhenFilledOnly) {
      validateTelephoneInput(errors, value);
      return;
    }

    validateTelephoneInput(errors, {
      ...value,
      error: invalidError ?? errorMessages?.required ?? PHONE_REQUIRED_ERROR,
      required: true,
      touched: true,
    });
  },
];

/** @type {PageSchema} */
export default {
  uiSchema: {
    [veteranFields.parentObject]: {
      ...titleUI(
        'Contact information',
        'How can we reach you? Enter at least 1 phone number. A phone number is required.',
      ),
      [veteranFields.homePhone]: {
        ...internationalPhoneUI({
          title: 'United States phone number',
          hint: 'Enter your 10-digit phone number, including the Area Code, such as 999-999-9999',
          noCountry: true,
        }),
        'ui:webComponentField': TelephoneFieldNoInternalErrors,
        'ui:required': isPhoneRequired(veteranFields.homePhone),
        'ui:errorMessages': {
          required: PHONE_REQUIRED_ERROR,
        },
        'ui:validations': createPhoneValidations({
          invalidError: PRIMARY_PHONE_INVALID_ERROR,
          requireUSCountry: true,
        }),
      },
      [veteranFields.alternatePhone]: {
        ...internationalPhoneUI({
          title: 'International phone number',
          hint: 'Select your country code and enter your phone number',
          initialCountry: 'AF',
          hideEmptyValueInReview: true,
        }),
        'ui:webComponentField': TelephoneFieldNoInternalErrors,
        'ui:required': isPhoneRequired(veteranFields.alternatePhone),
        'ui:errorMessages': {
          required: PHONE_REQUIRED_ERROR,
        },
        'ui:validations': createPhoneValidations({
          validateWhenFilledOnly: true,
        }),
      },
      [veteranFields.email]: {
        ...emailToSendNotificationsUI({
          title: 'Email address',
          hint: 'We’ll use this email address to confirm when we receive your form',
        }),
        'ui:required': () => true,
      },
      electronicCorrespondence: checkboxUI({
        title:
          'I agree to receive electronic correspondence from the VA about my claim.',
        classNames: 'custom-width',
      }),
    },
  },
  schema: {
    type: 'object',
    properties: {
      [veteranFields.parentObject]: {
        type: 'object',
        required: [veteranFields.email],
        properties: {
          [veteranFields.homePhone]: internationalPhoneSchema(),
          [veteranFields.alternatePhone]: internationalPhoneSchema(),
          [veteranFields.email]: emailToSendNotificationsSchema,
          electronicCorrespondence: checkboxSchema,
        },
      },
    },
  },
};
